import { Link } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import Modal from "@/components/Modal";
import MediaInput from "@/components/MediaInput";
import ExampleHint from "@/components/ExampleHint";
import PhoneField from "@/components/PhoneField";
import {
  Plus,
  Users,
  Pencil,
  Trash2,
  Lock,
  MessageSquareHeart,
  Bomb,
  Mail,
  Phone,
  X,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Database,
  Heart,
} from "lucide-react";

const EMPTY = {
  name: "",
  email: "",
  emails: [""],
  phone: "",
  relationship: "",
  // share_percent is set dynamically when opening "create" — defaults to the
  // remaining percentage available (100% if no heirs exist yet).
  share_percent: 100,
  legacy_message_body: "",
  legacy_media_type: "text",
  legacy_media_data: "",
  legacy_media_filename: "",
  legacy_media_size: 0,
  notify_heir: false,
};

// RFC 5322-ish practical regex (Pydantic EmailStr does the strict check server-side).
const EMAIL_RX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export default function Heirs() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [assignments, setAssignments] = useState({}); // heir_id → {vault, messages, capsule, assets}
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [summary, setSummary] = useState({ assigned: 0, remaining: 100, is_complete: false });

  const load = useCallback(async () => {
    const [h, v, m, c, a, s] = await Promise.all([
      api.get("/heirs"),
      api.get("/vault"),
      api.get("/messages"),
      api.get("/capsule"),
      api.get("/digital-assets"),
      api.get("/heirs/share-summary").catch(() => ({ data: null })),
    ]);
    setItems(h.data);
    const map = {};
    for (const heir of h.data) {
      map[heir.heir_id] = {
        vault: v.data.filter((x) => (x.assigned_heirs || []).includes(heir.heir_id)),
        messages: m.data.filter((x) => (x.assigned_heirs || []).includes(heir.heir_id)),
        capsule: c.data.filter((x) => (x.assigned_heirs || []).includes(heir.heir_id)),
        assets: a.data.filter((x) => (x.assigned_heirs || []).includes(heir.heir_id)),
      };
    }
    setAssignments(map);
    if (s.data) setSummary(s.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    // B4 — pre-fill with the remaining % available so the user doesn't have
    // to think (100 when no heirs yet, X when others already assigned).
    const sumOthers = items.reduce((acc, x) => acc + (parseInt(x.share_percent, 10) || 0), 0);
    const remaining = Math.max(0, 100 - sumOthers);
    setForm({ ...EMPTY, share_percent: remaining || 100 });
    setOpen(true);
  };

  const openEdit = (h) => {
    setEditingId(h.heir_id);
    setForm({
      name: h.name,
      email: h.email,
      emails: (h.emails && h.emails.length > 0) ? h.emails : [h.email || ""],
      phone: h.phone || "",
      relationship: h.relationship,
      share_percent: h.share_percent || 1,
      legacy_message_body: h.legacy_message_body || "",
      legacy_media_type: h.legacy_media_type || "text",
      legacy_media_data: h.legacy_media_data || "",
      legacy_media_filename: h.legacy_media_filename || "",
      legacy_media_size: h.legacy_media_size || 0,
      notify_heir: !!h.notify_heir,
    });
    setViewing(null);
    setOpen(true);
  };

  // B4 — remaining % available for the current heir (excluding their own share when editing)
  const remainingForForm = (() => {
    const others = items.filter((h) => h.heir_id !== editingId);
    const sumOthers = others.reduce((acc, x) => acc + (parseInt(x.share_percent, 10) || 0), 0);
    return Math.max(0, 100 - sumOthers);
  })();

  const save = async (e) => {
    e.preventDefault();
    const cleanEmails = form.emails.map((x) => x.trim()).filter(Boolean);
    if (cleanEmails.length === 0) {
      toast.error(t("heir_at_least_one_email"));
      return;
    }
    // B3 — RFC 5322 format check (Pydantic also enforces server-side)
    const bad = cleanEmails.find((em) => !EMAIL_RX.test(em));
    if (bad) {
      toast.error(t("heir_email_invalid").replace("{EMAIL}", bad));
      return;
    }
    // B4 — share validation
    const share = parseInt(form.share_percent, 10) || 0;
    if (share < 1 || share > 100) {
      toast.error(t("heir_share_range_err"));
      return;
    }
    if (share > remainingForForm) {
      toast.error(
        t("heir_share_overflow_err").replace("{REMAINING}", remainingForForm),
      );
      return;
    }
    const payload = {
      ...form,
      email: cleanEmails[0],
      emails: cleanEmails,
      share_percent: share,
      notify_heir: !!form.notify_heir,
    };
    try {
      if (editingId) {
        const r = await api.put(`/heirs/${editingId}`, payload);
        setItems((arr) => arr.map((x) => (x.heir_id === editingId ? r.data : x)));
      } else {
        const r = await api.post("/heirs", payload);
        setItems((arr) => [...arr, r.data]);
      }
      toast.success(t("saved"));
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      // Surface Pydantic validation errors cleanly
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const first = detail[0];
        toast.error(`${first.msg} (${(first.loc || []).join(".")})`);
      } else {
        toast.error(detail || "Error");
      }
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/heirs/${id}`);
    toast.success(t("deleted"));
    setItems((arr) => arr.filter((x) => x.heir_id !== id));
    if (viewing?.heir_id === id) setViewing(null);
  };

  return (
    <div data-testid="heirs-page">
      <PageHeader
        title={t("heir_title")}
        subtitle={t("heir_subtitle")}
        action={
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to="/digital-assets"
              className="btn-gold"
              data-testid="link-digital-assets"
            >
              <Database className="w-4 h-4" /> {t("heir_link_digital_assets")}
            </Link>
            <button className="btn-gold" onClick={openCreate} data-testid="heir-add-btn">
              <Plus className="w-4 h-4" /> {t("heir_add")}
            </button>
          </div>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={Users}>{t("heir_empty")}</EmptyState>
      ) : (
        <>
          {/* B4 — share summary bar */}
          <div className="glass-card rounded-md p-4 mb-4" data-testid="heir-share-summary">
            <div className="flex items-center justify-between mb-2 text-xs text-faded">
              <span>
                {t("heir_share_assigned").replace("{N}", summary.assigned)}
                {" · "}
                <span className={summary.remaining > 0 ? "text-yellow-400" : "text-emerald-400"}>
                  {t("heir_share_remaining").replace("{N}", summary.remaining)}
                </span>
              </span>
              {summary.is_complete && (
                <span className="text-[10px] uppercase tracking-wider text-emerald-400">
                  ✓ 100%
                </span>
              )}
            </div>
            <div className="h-2 rounded-full bg-obsidian-2/80 overflow-hidden flex">
              {items
                .filter((h) => (h.share_percent || 0) > 0)
                .map((h) => (
                  <div
                    key={h.heir_id}
                    title={`${h.name}: ${h.share_percent}%`}
                    className="h-full border-r border-obsidian"
                    style={{ width: `${h.share_percent}%`, background: "#C9A36A" }}
                  />
                ))}
              {summary.remaining > 0 && (
                <div
                  className="h-full"
                  style={{ width: `${summary.remaining}%`, background: "rgba(201,163,106,0.12)" }}
                />
              )}
            </div>
            {summary.remaining > 0 && (
              <p className="text-[11px] text-faded mt-2 italic">
                {t("heir_share_warning").replace("{N}", summary.remaining)}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((h) => {
            const a = assignments[h.heir_id] || { vault: [], messages: [], capsule: [], assets: [] };
            const total = a.vault.length + a.messages.length + a.capsule.length + a.assets.length;
            const hasFarewell = !!(h.legacy_message_body || h.legacy_media_data);
            const emailsCount = (h.emails && h.emails.length) || (h.email ? 1 : 0);
            const isComplete = emailsCount >= 2 && (hasFarewell || total > 0);
            return (
              <button
                key={h.heir_id}
                onClick={() => setViewing(h)}
                className="glass-card rounded-md p-5 text-left hover:border-gold transition group"
                data-testid={`heir-${h.heir_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-md bg-gold/10 border border-gold-subtle shrink-0">
                    <Users className="w-5 h-5 text-gold" strokeWidth={1.4} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-gold-light truncate text-base">{h.name}</div>
                      {isComplete ? (
                        <span className="text-[10px] uppercase tracking-wider bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded">
                          ✓ {t("heir_status_complete")}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider bg-yellow-500/10 text-yellow-400 px-2 py-0.5 rounded">
                          {t("heir_status_pending")}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted mt-1">
                      {h.relationship}
                      {h.share_percent ? ` · ${h.share_percent}%` : ""}
                    </div>

                    <div className="flex items-center gap-3 mt-3 text-xs text-faded flex-wrap">
                      <span className="flex items-center gap-1" title={t("heir_tooltip_emails")}>
                        <Mail className="w-3 h-3" /> {emailsCount}
                      </span>
                      {h.phone && (
                        <span className="flex items-center gap-1" title={t("heir_tooltip_sms")}>
                          <Phone className="w-3 h-3" />
                        </span>
                      )}
                      {hasFarewell && (
                        <span className="flex items-center gap-1 text-gold" title={t("heir_tooltip_farewell")}>
                          <Heart className="w-3 h-3 fill-gold" />
                        </span>
                      )}
                      {a.assets.length > 0 && (
                        <span className="flex items-center gap-1" title={t("heir_tooltip_assets")}>
                          <Database className="w-3 h-3" /> {a.assets.length}
                        </span>
                      )}
                      {a.vault.length > 0 && (
                        <span className="flex items-center gap-1" title={t("heir_tooltip_vault")}>
                          <Lock className="w-3 h-3" /> {a.vault.length}
                        </span>
                      )}
                      {a.messages.length > 0 && (
                        <span className="flex items-center gap-1" title={t("heir_tooltip_messages")}>
                          <MessageSquareHeart className="w-3 h-3" /> {a.messages.length}
                        </span>
                      )}
                      {a.capsule.length > 0 && (
                        <span className="flex items-center gap-1" title={t("heir_tooltip_capsule")}>
                          <Bomb className="w-3 h-3" /> {a.capsule.length}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
          </div>
        </>
      )}

      <ExampleHint slug="heirs" testid="heirs-hint" />

      {/* VIEW MODAL */}
      <Modal
        open={!!viewing && !open}
        onClose={() => setViewing(null)}
        title={viewing?.name || ""}
        testid="heir-view-modal"
      >
        {viewing && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label={t("heir_relationship")} value={viewing.relationship} />
              <Field label={t("heir_share")} value={`${viewing.share_percent || 0}%`} />
            </div>

            <div>
              <div className="font-label text-faded mb-2">{t("heir_emails_count")} ({(viewing.emails || [viewing.email]).filter(Boolean).length})</div>
              {(viewing.emails && viewing.emails.length ? viewing.emails : [viewing.email]).filter(Boolean).map((em, i) => (
                <div key={i} className="text-gold-light flex items-center gap-2 mb-1 text-sm">
                  <Mail className="w-3.5 h-3.5 text-gold" /> {em}
                  {i === 0 && <span className="text-[10px] text-faded">({t("heir_email_primary_tag")})</span>}
                </div>
              ))}
            </div>

            {viewing.phone && (
              <Field label={t("heir_phone_view_label")} value={viewing.phone} icon={Phone} />
            )}

            {(viewing.legacy_message_body || viewing.legacy_media_data) && (
              <div className="bg-gold/5 border border-gold-subtle rounded-md p-4">
                <div className="font-label text-gold flex items-center gap-2 mb-2">
                  <Heart className="w-3 h-3 fill-gold" /> {t("heir_farewell_title")}
                </div>
                {viewing.legacy_message_body && (
                  <p className="text-sm text-gold-light whitespace-pre-wrap leading-relaxed font-body italic">
                    "{viewing.legacy_message_body.slice(0, 200)}{viewing.legacy_message_body.length > 200 ? "…" : ""}"
                  </p>
                )}
                {viewing.legacy_media_data && (
                  <div className="mt-2 text-xs text-faded flex items-center gap-2">
                    📎 {viewing.legacy_media_filename || viewing.legacy_media_type}
                  </div>
                )}
              </div>
            )}

            {(() => {
              const a = assignments[viewing.heir_id] || { vault: [], messages: [], capsule: [], assets: [] };
              const total = a.vault.length + a.messages.length + a.capsule.length + a.assets.length;
              if (total === 0) {
                return (
                  <div className="bg-obsidian-2/60 border border-gold-subtle rounded-md p-4 text-sm text-muted italic">
                    {t("heir_nothing_assigned").replace("{NAME}", viewing.name)}
                  </div>
                );
              }
              return (
                <div className="bg-obsidian-2/60 border border-gold-subtle rounded-md p-4 space-y-3">
                  <div className="font-label text-gold flex items-center gap-2">
                    {t("heir_will_receive")}
                  </div>
                  <AssignedList icon={Database} label={t("heir_assigned_assets")} items={a.assets} keyField="asset_id" />
                  <AssignedList icon={Lock} label={t("heir_assigned_vault")} items={a.vault} keyField="item_id" />
                  <AssignedList icon={MessageSquareHeart} label={t("heir_assigned_messages")} items={a.messages} keyField="message_id" />
                  <AssignedList icon={Bomb} label={t("heir_assigned_capsule")} items={a.capsule} keyField="capsule_id" />
                </div>
              );
            })()}

            <div className="flex gap-3 justify-end pt-3 border-t border-gold-subtle">
              <button className="btn-ghost" onClick={() => remove(viewing.heir_id)} data-testid="heir-view-delete">
                <Trash2 className="w-4 h-4" /> {t("delete")}
              </button>
              <button className="btn-gold" onClick={() => openEdit(viewing)} data-testid="heir-view-edit">
                <Pencil className="w-4 h-4" /> {t("edit")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* CREATE/EDIT MODAL */}
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditingId(null);
        }}
        title={editingId ? t("edit") : t("heir_add")}
        testid="heir-modal"
      >
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="font-label text-faded block mb-2">{t("name")}</label>
              <input
                className="input-field"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="heir-name"
              />
            </div>
            <div>
              <label className="font-label text-faded block mb-2">{t("heir_relationship")}</label>
              <input
                className="input-field"
                required
                value={form.relationship}
                onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                data-testid="heir-relationship"
                placeholder={t("heir_relationship_ph")}
              />
            </div>
          </div>

          {/* Multi-email */}
          <div>
            <label className="font-label text-faded block mb-2">
              {t("heir_emails_label")}
            </label>
            <div className="space-y-2">
              {form.emails.map((em, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="email"
                    className="input-field flex-1"
                    required={i === 0}
                    value={em}
                    placeholder={i === 0 ? t("heir_email_primary_ph") : t("heir_email_backup_ph")}
                    onChange={(e) => {
                      const next = [...form.emails];
                      next[i] = e.target.value;
                      setForm({ ...form, emails: next });
                    }}
                    data-testid={`heir-email-${i}`}
                  />
                  {form.emails.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, emails: form.emails.filter((_, j) => j !== i) })}
                      className="btn-ghost px-3"
                      aria-label={t("heir_remove_email")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {form.emails.length < 5 && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, emails: [...form.emails, ""] })}
                  className="text-xs text-gold hover:text-gold-light flex items-center gap-1.5"
                  data-testid="heir-add-email"
                >
                  <Plus className="w-3 h-3" /> {t("heir_add_email")}
                </button>
              )}
            </div>
            <p className="text-[11px] text-faded mt-2">{t("heir_emails_help")}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="font-label text-faded block mb-2">
                {t("heir_phone_label")} <span className="text-faded">({t("optional")})</span>
              </label>
              <PhoneField
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                testid="heir-phone"
              />
            </div>
            <div>
              <label className="font-label text-faded block mb-2">
                {t("heir_share")} %
                <span className="text-[10px] text-faded ml-2 normal-case">
                  {t("heir_share_available").replace("{N}", remainingForForm)}
                </span>
              </label>
              <input
                type="number"
                min="1"
                max={remainingForForm || 100}
                className="input-field"
                placeholder="ej: 50"
                value={form.share_percent}
                onChange={(e) => setForm({ ...form, share_percent: e.target.value })}
                data-testid="heir-share"
                required
              />
              {(() => {
                const v = parseInt(form.share_percent, 10);
                if (Number.isNaN(v) || v <= 0) return null;
                if (v > remainingForForm) {
                  return (
                    <p className="text-xs text-red-400 mt-1.5" data-testid="heir-share-error">
                      {t("heir_share_overflow_err").replace("{REMAINING}", remainingForForm)}
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          </div>

          {/* B3 — opt-in heir notification (privacy-first: default OFF) */}
          {!editingId && (
            <label className="ks-checkbox cursor-pointer bg-obsidian-2/40 border border-gold-subtle rounded-md p-3">
              <input
                type="checkbox"
                checked={!!form.notify_heir}
                onChange={(e) => setForm({ ...form, notify_heir: e.target.checked })}
                data-testid="heir-notify"
              />
              <span className="ks-checkbox-box" aria-hidden="true"></span>
              <span className="text-xs leading-snug text-gold-light/90">
                <strong>{t("heir_notify_label")}</strong>
                <span className="block text-faded text-[11px] mt-1">
                  {t("heir_notify_help")}
                </span>
              </span>
            </label>
          )}

          {/* Legacy message */}
          <div className="border-l-4 border-l-gold pl-4 py-2 bg-gold/[0.03] rounded-r-md">
            <div className="font-display text-xl text-gold-light flex items-center gap-2 mb-1">
              <Heart className="w-4 h-4 fill-gold text-gold" /> {t("heir_farewell_title")}
            </div>
            <p className="text-xs text-muted mb-4">
              {t("heir_farewell_desc")}
            </p>
            <MediaInput
              textLabel={t("heir_farewell_input_label")}
              textValue={form.legacy_message_body}
              textRows={4}
              onTextChange={(v) => setForm({ ...form, legacy_message_body: v })}
              media={{
                media_type: form.legacy_media_type,
                media_data: form.legacy_media_data,
                media_filename: form.legacy_media_filename,
                media_size: form.legacy_media_size,
              }}
              onMediaChange={(m) =>
                setForm({
                  ...form,
                  legacy_media_type: m.media_type,
                  legacy_media_data: m.media_data,
                  legacy_media_filename: m.media_filename,
                  legacy_media_size: m.media_size,
                })
              }
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
              {t("cancel")}
            </button>
            <button type="submit" className="btn-gold" data-testid="heir-save">
              {t("save")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="font-label text-faded mb-1">{label}</div>
      <div className="text-gold-light break-all flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-gold" />}
        {value}
      </div>
    </div>
  );
}

function AssignedList({ icon: Icon, label, items, keyField }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs text-faded mb-1 flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-gold" /> {label} ({items.length})
      </div>
      <ul className="text-sm text-gold-light space-y-1 pl-5">
        {items.map((x) => (
          <li key={x[keyField]} className="list-disc">{x.title}</li>
        ))}
      </ul>
    </div>
  );
}
